import type {
  HistoryState,
  NavigationFailure,
  RouteLocationRaw,
  Router,
} from 'vue-router'

export interface ReturnNavigationState extends HistoryState {
  appBackTo: string
  appBackPosition: number
}

type RouteLocationWithState = Exclude<RouteLocationRaw, string> & {
  state?: HistoryState
}

export function isSafeInternalReturnTarget(value: unknown): value is string {
  return typeof value === 'string'
    && value.startsWith('/')
    && !value.startsWith('//')
    && !value.startsWith('/\\')
    && !Array.from(value).some((character) => {
      const code = character.charCodeAt(0)
      return code <= 31 || code === 127
    })
}

function historyPosition(router: Router): number {
  const position = router.options.history.state.position
  return typeof position === 'number' && Number.isInteger(position) ? position : -1
}

export function withReturnTarget(
  router: Router,
  target: RouteLocationRaw,
  sourceFullPath: string,
): RouteLocationRaw {
  const appBackTo = isSafeInternalReturnTarget(sourceFullPath) ? sourceFullPath : '/'
  const returnState: ReturnNavigationState = {
    appBackTo,
    appBackPosition: historyPosition(router),
  }

  if (typeof target === 'string') {
    const resolved = router.resolve(target)
    return {
      path: resolved.path,
      query: resolved.query,
      hash: resolved.hash,
      state: returnState,
    }
  }

  const targetWithState = target as RouteLocationWithState
  return {
    ...targetWithState,
    state: {
      ...targetWithState.state,
      ...returnState,
    },
  } as RouteLocationRaw
}

export function pushWithReturnTarget(
  router: Router,
  target: RouteLocationRaw,
  sourceFullPath: string,
): Promise<NavigationFailure | void | undefined> {
  return router.push(withReturnTarget(router, target, sourceFullPath))
}

function canUseNativeBack(router: Router, state: HistoryState, target: string): boolean {
  const currentPosition = state.position
  const sourcePosition = state.appBackPosition
  const browserBack = state.back
  if (
    typeof currentPosition !== 'number'
    || typeof sourcePosition !== 'number'
    || currentPosition !== sourcePosition + 1
    || typeof browserBack !== 'string'
  ) return false

  return router.resolve(browserBack).fullPath === router.resolve(target).fullPath
}

export async function navigateBackFromSubPage(
  router: Router,
  metaBackTo: string | undefined,
  roleFallback: string,
): Promise<void> {
  const state = router.options.history.state
  const contextualTarget = isSafeInternalReturnTarget(state.appBackTo)
    ? state.appBackTo
    : undefined

  if (contextualTarget) {
    if (canUseNativeBack(router, state, contextualTarget)) {
      router.back()
      return
    }
    await router.replace(contextualTarget)
    return
  }

  if (isSafeInternalReturnTarget(metaBackTo)) {
    await router.replace(metaBackTo)
    return
  }

  await router.replace(roleFallback)
}
